// The Expense Bills page (V-09, ADR-0035, under Money): every supplier's bill for an expense, newest first, with where it
// stands in plain words, on whom it waits by Role, its amounts and what it belongs to; filtered by status, supplier, a
// Dimension value and dates, a page at a time, with the totals of every bill the filters keep: the posted bills' apart
// from those waiting to be confirmed or approved, each saying what it counts, and a held or refused bill in neither. A row opens the bill:
// its lines, its file, its posting once posted and its Run. "Add a bill" sends a file as attaching it in chat does,
// read as a supplier's bill without asking what it is, and the list shows it waiting to be confirmed. One LAMP couldn't
// read is tried again, entered by hand or said to be no bill on the page itself (#1006). On a phone each row stacks,
// so the page never scrolls sideways.
import { useEffect, useRef, useState, type ChangeEvent, type FormEvent } from "react";
import { rupees } from "../api.js";
import { base64Of, checkFile, ACCEPT, MAX_FILE_MEGABYTES } from "../chat/attach.js";
import { ApiError } from "../http.js";
import { useWords } from "../i18n/words.js";
import type { WordKey } from "../shell/nav.js";
import {
  STANDINGS,
  createBillsApi,
  type BillAdded,
  type BillList,
  type BillShown,
  type BillsApi,
  type BillsAsked,
  type NamedValue,
  type Standing,
  type TypedBill,
} from "./api.js";

/** Where a bill stands, in the person's words. */
export const STANDING_WORDS: Readonly<Record<Standing, WordKey>> = {
  to_confirm: "billsToConfirm",
  to_approve: "billsToApprove",
  to_confirm_tax: "billsToConfirmTax",
  unpaid: "billsUnpaid",
  paid: "billsPaid",
  held: "billsHeld",
};

const problemOf = (error: unknown, fallback: string) =>
  error instanceof ApiError && error.problems.length > 0 ? error.problems.join(" ") : fallback;

/** Dimension values as the page lists them: "Expense belongs to: Casa Azul Pvt Ltd". */
const valuesText = (values: readonly NamedValue[]) => values.map((v) => `${v.label}: ${v.name}`).join("; ");

/** An amount, or nothing where it is hidden. */
const amount = (paise: number | undefined) => (paise === undefined ? "" : rupees(paise));

function BillPanel({
  bill,
  onClose,
  onOpenRun,
}: {
  bill: BillShown;
  onClose: () => void;
  onOpenRun?: ((run: string) => void) | undefined;
}) {
  const w = useWords();
  const heading = `${w.billsNumber} ${bill.number} · ${bill.supplier.name}`;
  const tax = [bill.cgst, bill.sgst, bill.igst].reduce<number | undefined>(
    (sum, part) => (part === undefined ? sum : (sum ?? 0) + part),
    undefined,
  );
  return (
    <section className="bill-panel" aria-label={heading}>
      <h3>{heading}</h3>
      <dl>
        <dt>{w.billsStatus}</dt>
        <dd>{w[STANDING_WORDS[bill.standing]]}</dd>
        {bill.holdReason ? (
          <>
            <dt>{w.billsHeld}</dt>
            <dd>{w.billsHeldBecause(bill.holdReason)}</dd>
          </>
        ) : null}
        <dt>{w.billsDate}</dt>
        <dd>{bill.date}</dd>
        {bill.waitsOn.length > 0 || bill.yours ? (
          <>
            <dt>{w.billsWaitsOn}</dt>
            <dd>{bill.yours ? w.billsYou : bill.waitsOn.join(", ")}</dd>
          </>
        ) : null}
        {tax === undefined ? null : (
          <>
            <dt>{w.billsTax}</dt>
            <dd>{rupees(tax)}</dd>
          </>
        )}
        {bill.total === undefined ? null : (
          <>
            <dt>{w.billsTotal}</dt>
            <dd>{rupees(bill.total)}</dd>
            <dt>{w.billsTds}</dt>
            <dd>{amount(bill.tds)}</dd>
            <dt>{w.billsPayable}</dt>
            <dd>{amount(bill.payable)}</dd>
          </>
        )}
      </dl>
      <h4>{w.billsLines}</h4>
      <ul className="bill-lines">
        {bill.lines.map((line, i) => (
          <li key={i}>
            <strong>{line.description}</strong>
            {line.amount === undefined ? null : <span className="amount"> {rupees(line.amount)}</span>}
            <br />
            <span className="muted">
              {w.billsAccount}: {line.accountName || "—"}
            </span>
            {line.values.length > 0 ? (
              <>
                <br />
                <span>{valuesText(line.values)}</span>
              </>
            ) : null}
            {line.split ? (
              <ul aria-label={w.billsSplit}>
                {line.split.map((part, j) => (
                  <li key={j}>
                    {valuesText(part.values)}
                    {part.amount === undefined ? null : ` · ${rupees(part.amount)}`}
                  </li>
                ))}
              </ul>
            ) : null}
          </li>
        ))}
      </ul>
      {bill.file ? (
        <p>
          {w.billsFile}:{" "}
          <a href={bill.file.href} target="_blank" rel="noreferrer">
            {w.billsOpenFile(bill.file.name)}
          </a>
        </p>
      ) : null}
      {bill.postings ? (
        <>
          <h4>{w.billsPostings}</h4>
          <ul className="bill-lines">
            {bill.postings.map((p, i) => (
              <li key={i}>
                {p.name} <span className="amount">{rupees(p.amount)}</span>
              </li>
            ))}
          </ul>
        </>
      ) : bill.standing === "unpaid" || bill.standing === "paid" ? null : (
        <p className="muted">{w.billsNotPosted}</p>
      )}
      <p className="bill-actions">
        {bill.run && onOpenRun ? (
          <button type="button" className="btn" onClick={() => onOpenRun(bill.run ?? "")}>
            {w.billsOpenRun}
          </button>
        ) : null}{" "}
        <button type="button" className="btn" onClick={onClose}>
          {w.billsClose}
        </button>
      </p>
    </section>
  );
}

/** Rupees as typed ("11,800", "1180.50") in paise; undefined when it is not an amount. */
export const paiseTyped = (typed: string): number | undefined => {
  const plain = typed.replace(/^₹\s*/, "").replaceAll(",", "").trim();
  const match = /^(\d+)(?:\.(\d{1,2}))?$/.exec(plain);
  return match ? Number(match[1]) * 100 + Number((match[2] ?? "").padEnd(2, "0")) : undefined;
};

/** A bill LAMP couldn't read, typed in: who it is from, its number and date, what it is for and its amounts. */
function TypedBillForm({
  name,
  busy,
  onEnter,
  onCancel,
}: {
  name: string;
  busy: boolean;
  onEnter: (bill: TypedBill) => void;
  onCancel: () => void;
}) {
  const w = useWords();
  const [problem, setProblem] = useState<string | undefined>(undefined);
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const field = (key: string) => String(form.get(key) ?? "").trim();
    const amounts = { amount: w.billsTypedAmount, gst: w.billsTypedGst, total: w.billsTypedTotal };
    const paise: Record<string, number> = {};
    for (const [key, label] of Object.entries(amounts)) {
      const value = paiseTyped(field(key) === "" && key === "gst" ? "0" : field(key));
      if (value === undefined) {
        setProblem(w.billsTypedNotRupees(label));
        return;
      }
      paise[key] = value;
    }
    const { amount = 0, gst = 0, total = 0 } = paise;
    if (amount + gst !== total) {
      setProblem(w.billsTypedAddsUp);
      return;
    }
    setProblem(undefined);
    onEnter({
      supplier: field("supplier"),
      number: field("number"),
      date: field("date"),
      description: field("description"),
      amount,
      gst,
      total,
    });
  };
  return (
    <form className="send-back bills-typed" aria-label={w.billsTyped(name)} onSubmit={submit}>
      <label className="field">
        {w.billsTypedSupplier}
        <input name="supplier" required maxLength={200} autoComplete="off" />
      </label>
      <label className="field">
        {w.billsTypedNumber}
        <input name="number" required maxLength={50} autoComplete="off" />
      </label>
      <label className="field">
        {w.billsTypedDate}
        <input name="date" type="date" required />
      </label>
      <label className="field">
        {w.billsTypedFor}
        <input name="description" required maxLength={200} />
      </label>
      <label className="field">
        {w.billsTypedAmount}
        <input name="amount" required inputMode="decimal" />
      </label>
      <label className="field">
        {w.billsTypedGst}
        <input name="gst" inputMode="decimal" />
      </label>
      <label className="field">
        {w.billsTypedTotal}
        <input name="total" required inputMode="decimal" />
      </label>
      {problem ? <p role="alert">{problem}</p> : null}
      <div className="acts">
        <button type="submit" className="btn btn-go" disabled={busy}>
          {w.billsTypedDraft}
        </button>
        <button type="button" className="btn" onClick={onCancel}>
          {w.billsTypedCancel}
        </button>
      </div>
    </form>
  );
}

const defaultApi = createBillsApi();

export function Bills({
  api = defaultApi,
  onOpenRun,
}: {
  api?: BillsApi;
  onOpenRun?: ((run: string) => void) | undefined;
}) {
  const w = useWords();
  const [asked, setAsked] = useState<BillsAsked>({});
  const [reload, setReload] = useState(0);
  const [list, setList] = useState<BillList | undefined>(undefined);
  const [problem, setProblem] = useState<string | undefined>(undefined);
  const [opened, setOpened] = useState<BillShown | undefined>(undefined);
  const [openProblem, setOpenProblem] = useState<string | undefined>(undefined);
  const [adding, setAdding] = useState(false);
  const [said, setSaid] = useState<string | undefined>(undefined);
  const [addProblem, setAddProblem] = useState<string | undefined>(undefined);
  // The bill added that LAMP couldn't draft, while the page can do something with it; and its form, when typed in.
  const [pending, setPending] = useState<BillAdded["file"]>(undefined);
  const [entering, setEntering] = useState(false);
  const picker = useRef<HTMLInputElement>(null);

  useEffect(() => {
    let live = true;
    setProblem(undefined);
    api.bills(asked).then(
      (found) => live && setList(found),
      (error: unknown) => {
        if (!live) return;
        setList(undefined);
        setProblem(problemOf(error, w.billsLoadFailed));
      },
    );
    return () => {
      live = false;
    };
  }, [api, asked, reload]);

  /** A filter changed: the list starts again from its first page. */
  const filter = (change: Partial<BillsAsked>) => setAsked((now) => ({ ...now, ...change, page: undefined }));

  function open(id: string) {
    setOpenProblem(undefined);
    api.bill(id).then(setOpened, (error: unknown) => {
      setOpened(undefined);
      setOpenProblem(problemOf(error, w.billsLoadFailed));
    });
  }

  async function add(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    setSaid(undefined);
    setAddProblem(undefined);
    const checked = checkFile(file);
    if (!checked.ok) {
      setAddProblem(
        checked.reason === "type"
          ? w.chatFileType(file.name)
          : checked.reason === "size"
            ? w.chatFileSize(file.name, MAX_FILE_MEGABYTES)
            : w.chatFileEmpty(file.name),
      );
      return;
    }
    setPending(undefined);
    setEntering(false);
    const data = await base64Of(file);
    await addWith(() => api.add({ name: file.name, mediaType: checked.mediaType, data }, w.billsAddWords));
  }

  /** What became of the bill added, said on the page with what can be done next; the list shows it once drafted. */
  async function addWith(call: () => Promise<BillAdded>) {
    setAdding(true);
    setAddProblem(undefined);
    try {
      const added = await call();
      setSaid(added.said);
      setPending(added.file);
      setEntering(false);
      setReload((n) => n + 1);
    } catch (error) {
      // Refused (a typed bill that doesn't add up, say): the reason is said, and the bill waits as it was.
      setAddProblem(problemOf(error, w.billsLoadFailed));
    } finally {
      setAdding(false);
    }
  }

  const filtered = Object.entries(asked).some(([key, value]) => key !== "page" && value !== undefined);

  const filters = (
    <form className="bills-filters" aria-label={w.billsFilters} onSubmit={(event) => event.preventDefault()}>
      <label>
        {w.billsStandingFilter}{" "}
        <select
          value={asked.standing ?? ""}
          onChange={(event) => filter({ standing: STANDINGS.find((s) => s === event.target.value) ?? undefined })}
        >
          <option value="">{w.billsAnyStanding}</option>
          {STANDINGS.map((s) => (
            <option key={s} value={s}>
              {w[STANDING_WORDS[s]]}
            </option>
          ))}
        </select>
      </label>
      <label>
        {w.billsSupplierFilter}{" "}
        <select
          value={asked.supplier ?? ""}
          onChange={(event) => filter({ supplier: event.target.value || undefined })}
        >
          <option value="">{w.billsAnySupplier}</option>
          {(list?.suppliers ?? []).map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </select>
      </label>
      {list && list.dimensions.length > 0 ? (
        <label>
          {w.billsValueFilter}{" "}
          <select value={asked.value ?? ""} onChange={(event) => filter({ value: event.target.value || undefined })}>
            <option value="">{w.billsAnyValue}</option>
            {list.dimensions.map((d) => (
              <optgroup key={d.id} label={d.label}>
                {d.values.map((v) => (
                  <option key={v.id} value={v.id}>
                    {v.name}
                  </option>
                ))}
              </optgroup>
            ))}
          </select>
        </label>
      ) : null}
      <label>
        {w.billsFrom}{" "}
        <input
          type="date"
          value={asked.from ?? ""}
          onChange={(event) => filter({ from: event.target.value || undefined })}
        />
      </label>
      <label>
        {w.billsTo}{" "}
        <input
          type="date"
          value={asked.to ?? ""}
          onChange={(event) => filter({ to: event.target.value || undefined })}
        />
      </label>
    </form>
  );

  return (
    <>
      <div className="hello">
        <h2>{w.pageBills}</h2>
        <p className="muted">{w.billsIntro}</p>
      </div>
      <p className="bills-add">
        <button type="button" className="btn btn-go" disabled={adding} onClick={() => picker.current?.click()}>
          {w.billsAdd}
        </button>
        <input ref={picker} type="file" accept={ACCEPT} hidden aria-label={w.billsAdd} onChange={(e) => void add(e)} />
      </p>
      {adding ? <p role="status">{w.billsAdding}</p> : null}
      {said ? (
        <p role="status" className="bills-said">
          {said}
        </p>
      ) : null}
      {pending ? (
        <div className="bills-pending">
          <p className="acts">
            {pending.actions.includes("retry") ? (
              <button
                type="button"
                className="btn btn-go"
                disabled={adding}
                onClick={() => void addWith(() => api.retry(pending.id))}
              >
                {w.billsTryAgain}
              </button>
            ) : null}{" "}
            {pending.actions.includes("enter") && !entering ? (
              <button type="button" className="btn" disabled={adding} onClick={() => setEntering(true)}>
                {w.billsEnterByHand}
              </button>
            ) : null}{" "}
            {pending.actions.includes("choose") ? (
              <button
                type="button"
                className="btn"
                disabled={adding}
                onClick={() => void addWith(() => api.notABill(pending.id))}
              >
                {w.billsNotABill}
              </button>
            ) : null}
          </p>
          {pending.actions.includes("choose") ? <p className="muted">{w.billsNotABillHint}</p> : null}
          {entering ? (
            <TypedBillForm
              name={pending.name}
              busy={adding}
              onEnter={(bill) => void addWith(() => api.enter(pending.id, bill))}
              onCancel={() => setEntering(false)}
            />
          ) : null}
        </div>
      ) : null}
      {addProblem ? <p role="alert">{addProblem}</p> : null}
      {list && (list.count > 0 || filtered) ? filters : null}
      {!list ? (
        problem ? (
          <p role="alert">{problem}</p>
        ) : (
          <p role="status">{w.billsLoading}</p>
        )
      ) : list.count === 0 ? (
        <p>{filtered ? w.billsNoneMatch : w.billsEmpty}</p>
      ) : (
        <>
          {list.hidden?.includes("amounts") ? <p className="muted">{w.billsAmountsHidden}</p> : null}
          <div className="tbl-wrap">
            <table className="tbl bills-table">
              <thead>
                <tr>
                  <th scope="col">{w.billsDate}</th>
                  <th scope="col">{w.billsNumber}</th>
                  <th scope="col">{w.billsSupplier}</th>
                  {list.totals ? (
                    <>
                      <th scope="col">{w.billsTotal}</th>
                      <th scope="col">{w.billsTds}</th>
                      <th scope="col">{w.billsPayable}</th>
                    </>
                  ) : null}
                  <th scope="col">{w.billsStatus}</th>
                  <th scope="col">{w.billsWaitsOn}</th>
                  <th scope="col">{w.billsBelongsTo}</th>
                </tr>
              </thead>
              <tbody>
                {list.bills.map((bill) => (
                  <tr key={bill.id}>
                    <td data-label={w.billsDate}>{bill.date}</td>
                    <td data-label={w.billsNumber}>
                      <button
                        type="button"
                        className="linkish"
                        aria-label={w.billsOpen(bill.number, bill.supplier.name)}
                        onClick={() => open(bill.id)}
                      >
                        {bill.number}
                      </button>
                    </td>
                    <td data-label={w.billsSupplier}>{bill.supplier.name}</td>
                    {list.totals ? (
                      <>
                        <td data-label={w.billsTotal} className="amount">
                          {amount(bill.total)}
                        </td>
                        <td data-label={w.billsTds} className="amount">
                          {amount(bill.tds)}
                        </td>
                        <td data-label={w.billsPayable} className="amount">
                          {amount(bill.payable)}
                        </td>
                      </>
                    ) : null}
                    <td data-label={w.billsStatus}>{w[STANDING_WORDS[bill.standing]]}</td>
                    <td data-label={w.billsWaitsOn}>{bill.yours ? w.billsYou : bill.waitsOn.join(", ")}</td>
                    <td data-label={w.billsBelongsTo}>{valuesText(bill.values)}</td>
                  </tr>
                ))}
              </tbody>
              {list.totals ? (
                <tfoot>
                  <tr>
                    <th scope="row" colSpan={3}>
                      {w.billsTotals(list.posted)}
                    </th>
                    <td data-label={w.billsTotal} className="amount">
                      {rupees(list.totals.total)}
                    </td>
                    <td data-label={w.billsTds} className="amount">
                      {rupees(list.totals.tds)}
                    </td>
                    <td data-label={w.billsPayable} className="amount">
                      {rupees(list.totals.payable)}
                    </td>
                    <td colSpan={3} />
                  </tr>
                  {list.waiting.count > 0 ? (
                    <tr>
                      <th scope="row" colSpan={3}>
                        {w.billsWaitingTotal(list.waiting.count)}
                      </th>
                      <td data-label={w.billsTotal} className="amount">
                        {rupees(list.waiting.total ?? 0)}
                      </td>
                      <td colSpan={5} />
                    </tr>
                  ) : null}
                  {list.neither > 0 ? (
                    <tr>
                      <td colSpan={9} className="muted">
                        {w.billsInNeither(list.neither)}
                      </td>
                    </tr>
                  ) : null}
                </tfoot>
              ) : null}
            </table>
          </div>
          {list.pages > 1 ? (
            <p className="bills-pages">
              <button
                type="button"
                className="btn"
                disabled={list.page <= 1}
                onClick={() => setAsked((now) => ({ ...now, page: list.page - 1 }))}
              >
                {w.billsPrevious}
              </button>{" "}
              <span>{w.billsPage(list.page, list.pages)}</span>{" "}
              <button
                type="button"
                className="btn"
                disabled={list.page >= list.pages}
                onClick={() => setAsked((now) => ({ ...now, page: list.page + 1 }))}
              >
                {w.billsNext}
              </button>
            </p>
          ) : null}
        </>
      )}
      {openProblem ? <p role="alert">{openProblem}</p> : null}
      {opened ? <BillPanel bill={opened} onClose={() => setOpened(undefined)} onOpenRun={onOpenRun} /> : null}
    </>
  );
}
